/**
 * MonthlyView Component
 * Monthly calendar grid showing event summary
 */

import React, { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Spin, Badge, Tooltip } from 'antd';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import type { CalendarEvent } from '@/api/types/calendars.types';
import { useSunTimes } from '@/hooks/useCalendars';
import './MonthlyView.css';

interface MonthlyViewProps {
  /** Current date (any day in the month) */
  currentDate: Date;
  /** Events to display */
  events: CalendarEvent[];
  /** Loading state */
  loading?: boolean;
  /** Callback when day is clicked */
  onDayClick?: (date: Date) => void;
  /** Callback when event is clicked */
  onEventClick?: (event: CalendarEvent) => void;
  /** Airport ID for sun times (optional) */
  airportId?: string;
  /** Whether to show sunrise/sunset overlay */
  showDayNight?: boolean;
}

/**
 * Get calendar grid (6 weeks x 7 days)
 */
const getCalendarGrid = (date: Date): Dayjs[][] => {
  const startOfMonth = dayjs(date).startOf('month');
  const startOfCalendar = startOfMonth.startOf('week');

  const weeks: Dayjs[][] = [];
  let currentDay = startOfCalendar;

  for (let week = 0; week < 6; week++) {
    const days: Dayjs[] = [];
    for (let day = 0; day < 7; day++) {
      days.push(currentDay);
      currentDay = currentDay.add(1, 'day');
    }
    weeks.push(days);
  }

  return weeks;
};

/**
 * Group events by day
 */
const groupEventsByDay = (
  events: CalendarEvent[],
  date: Dayjs
): CalendarEvent[] => {
  const dayStart = date.startOf('day').toDate();
  const dayEnd = date.endOf('day').toDate();

  return events.filter((event) => {
    const eventStart = new Date(event.start_time);
    const eventEnd = new Date(event.end_time);
    return (
      (eventStart >= dayStart && eventStart < dayEnd) ||
      (eventEnd > dayStart && eventEnd <= dayEnd) ||
      (eventStart <= dayStart && eventEnd >= dayEnd)
    );
  });
};

/**
 * Get event color from airplane resource metadata
 * Fallback to default blue if no airplane or no color
 */
const getEventColor = (event: CalendarEvent): string => {
  const airplane = event.resources?.find((r) => r.resource_type === 'AIRPLANE');
  const color = airplane?.metadata?.calendar_color;
  return color || '#1890ff';
};

/**
 * Generate sun times tooltip content
 * Displays times in user's timezone
 */
const generateSunTimesTooltip = (
  date: Dayjs,
  sunTimesMap: Record<string, any>,
  formatTime: (date: string, format: string) => string
): React.ReactNode => {
  const dateKey = date.format('YYYY-MM-DD');
  const sunTimes = sunTimesMap[dateKey];

  if (!sunTimes) return null;

  // Convert UTC times to user's timezone for display
  const sunrise = formatTime(sunTimes.sunrise, 'HH:mm');
  const sunset = formatTime(sunTimes.sunset, 'HH:mm');
  const civilBegin = formatTime(sunTimes.civil_twilight_begin, 'HH:mm');
  const civilEnd = formatTime(sunTimes.civil_twilight_end, 'HH:mm');
  const dayLengthHours = Math.floor(sunTimes.day_length / 3600);
  const dayLengthMinutes = Math.floor((sunTimes.day_length % 3600) / 60);

  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        ☀️ {date.format('MMM D, YYYY')}
      </div>
      <div style={{ marginBottom: 4 }}>
        <strong>Sunrise:</strong> {sunrise}
      </div>
      <div style={{ marginBottom: 4 }}>
        <strong>Sunset:</strong> {sunset}
      </div>
      <div
        style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 6 }}
      >
        <div style={{ marginBottom: 4, fontSize: 11, opacity: 0.9 }}>
          VFR Daylight: {civilBegin} - {civilEnd}
        </div>
        <div style={{ fontSize: 11, opacity: 0.9 }}>
          Day length: {dayLengthHours}h {dayLengthMinutes}m
        </div>
      </div>
    </div>
  );
};

export const MonthlyView: React.FC<MonthlyViewProps> = ({
  currentDate,
  events,
  loading = false,
  onDayClick,
  onEventClick,
  airportId,
  showDayNight,
}) => {
  const { formatTime } = useUserTimezone();
  const current = dayjs(currentDate);
  const calendarGrid = getCalendarGrid(currentDate);

  // Fetch sun times for entire month (42 days = 6 weeks) with single range query
  // Only fetches when airportId is defined (single airport selected) AND showDayNight is enabled
  const firstDay = calendarGrid[0][0];
  const lastDay = calendarGrid[5][6];

  const { data: sunTimesData } = useSunTimes(
    airportId || '',
    {
      date_from: firstDay.format('YYYY-MM-DD'),
      date_to: lastDay.format('YYYY-MM-DD'),
    },
    showDayNight && !!airportId
  );

  // Create a map for quick lookup by date
  const sunTimesMap = useMemo(() => {
    if (!sunTimesData) return {};

    // Handle range response
    if ('items' in sunTimesData) {
      return Object.fromEntries(
        sunTimesData.items.map((item) => [item.date, item])
      );
    }

    // Handle single date response (shouldn't happen, but type-safe)
    return { [sunTimesData.date]: sunTimesData };
  }, [sunTimesData]);

  if (loading) {
    return (
      <div className="monthly-view-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="monthly-view">
      <div className="month-grid">
        {/* Day headers */}
        <div className="month-header">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="month-header-cell">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="month-body">
          {calendarGrid.flatMap((week, weekIndex) =>
            week.map((day, dayIndex) => {
              const isCurrentMonth = day.month() === current.month();
              const isToday = day.isSame(dayjs(), 'day');
              const dayEvents = groupEventsByDay(events, day);

              return (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className={`
                      month-day
                      ${!isCurrentMonth ? 'month-day-other' : ''}
                      ${isToday ? 'month-day-today' : ''}
                    `}
                  onClick={() => onDayClick && onDayClick(day.toDate())}
                >
                  <Tooltip
                    title={generateSunTimesTooltip(
                      day,
                      sunTimesMap,
                      formatTime
                    )}
                    placement="top"
                    color="rgba(0, 0, 0, 0.85)"
                  >
                    <div className="month-day-number">
                      {isToday ? (
                        <Badge dot color="blue">
                          {day.date()}
                        </Badge>
                      ) : (
                        day.date()
                      )}
                    </div>
                  </Tooltip>

                  <div className="month-day-events">
                    {dayEvents.slice(0, 3).map((event) => {
                      const eventColor = getEventColor(event);
                      return (
                        <div
                          key={event.id}
                          className="month-event"
                          style={{ background: eventColor }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick && onEventClick(event);
                          }}
                        >
                          <span className="event-dot"></span>
                          <span className="event-text">
                            {formatTime(event.start_time, 'HH:mm')}{' '}
                            {event.title}
                          </span>
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className="month-more-events">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
